<template>
  <b-container class="user-list">
    <b-row class="mb-3">
      <b-col cols="12">
        <h2 class="d-inline-block">Users</h2>
        <b-btn
            class="float-right"
            variant="primary"
            @click="$router.push(`/admin/user/add`)"
        >
          Add User
        </b-btn>
      </b-col>
    </b-row>
    <v-wait for="loading">
      <template slot="waiting">
        <HalfCircleSpinner/>
        <div class="spinner-message my-3 text-center">Loading the users...</div>
      </template>
      <DataTable
          v-if="users.length"
          ref="dataTable"
          :show-filters="true"
          :show-pager="true"
          :items="filteredUsers"
          :filters="filters"
          :filter-title="'Filter Users'"
          :fields="fields"
          :no-local-sorting="false"
      >
        <!-- template for filters -->
        <template slot="filters">
          <b-row class="px-2">
            <b-col lg="6" md="12" class="mb-2">
              <label>Email</label>
              <b-form-input v-model="filters.email.value"/>
            </b-col>
            <b-col lg="6" md="12" class="mb-2">
              <label>Society</label>
              <b-form-select
                  v-model="filters.society_id.value"
                  :options="societyOptions"
              />
            </b-col>
          </b-row>
          <b-row class="px-2">
            <b-col lg="6" md="12" class="mb-2">
              <label>Role</label>
              <b-form-select
                  v-model="filters.role.value"
                  :options="roleOptions"
              />
            </b-col>
              <b-col lg="6" md="12" class="mb-2">
                  <label>Username</label>
                  <b-form-input v-model="filters.username.value"/>
              </b-col>
          </b-row>
        </template>

        <!-- templates for custom columns -->

        <template slot="email" slot-scope="data">
          {{ data.item.email }}
          <b-badge
              variant="danger"
              class="mr-2"
              v-if="`${data.item.disabled}` !== '0'"
          >
            Disabled
          </b-badge>
          <b-badge
              variant="success"
              class="mr-2"
              v-if="data.item.role == 'ADMIN'"
          >
            Admin
          </b-badge>
        </template>

          <template slot="username" slot-scope="data">
              {{ data.item.username }}
              <b-badge
                      variant="danger"
                      class="mr-2"
                      v-if="`${data.item.disabled}` !== '0'"
              >
                  Disabled
              </b-badge>
          </template>

        <template slot="society_id" slot-scope="data">
          {{ data.item.society ? `${data.item.society.url_code}` : '' }}
        </template>

        <template slot="specialty_id" slot-scope="data">
          {{ data.item.specialty ? `${data.item.specialty.code}` : '' }}
        </template>

        <template slot="subspecialty_id" slot-scope="data">
          {{ data.item.subspecialty ? `${data.item.subspecialty.code}` : '' }}
        </template>

        <template slot="actions" slot-scope="data">
          <div class="d-flex align-items-center justify-content-center">
            <b-btn size="sm" variant="default">
              <span
                  v-show="data.item.id"
                  title="Edit User"
                  @click="editedUserId = data.item.id"
              >
                <i class="mdi mdi-lead-pencil"/>
              </span>
            </b-btn>
            <b-btn size="sm" variant="default" @click="onDeleteUser(data.item)">
              <i class="mdi mdi-account-remove"/>
            </b-btn>
          </div>
        </template>
      </DataTable>
    </v-wait>
    <b-modal
        :visible="!!editedUserId"
        size="lg"
        hide-footer
        class="text-left"
        @close="editedUserId = null"
    >
      <template v-if="editedUser" slot="modal-header">
        <h5 class="modal-title">
          Edit User #{{ editedUser.id }} {{ editedUser.email }}
        </h5>
        <div class="pull-right">
          <b-button variant="default" @click="editedUserId = null">
            Cancel
          </b-button>
          <b-button
              @click.prevent="onSaveHandler"
              variant="primary"
              class="mr-2"
              :disabled="saveLoading"
          >
            <template v-if="!saveLoading">Save</template>
            <template v-else>
              <b-spinner small/>
              <span class="ml-1">Saving...</span>
            </template>
          </b-button>
        </div>
      </template>
      <user-edit
          v-if="editedUserId"
          :key="editedUserId"
          :initial-user="editedUser"
          ref="editUserForm"
          @reset-user="onResetUser"
      />
    </b-modal>
  </b-container>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Vue from 'vue';
import DataTable from '@/components/DataTable';
import UserEdit from '@/components/users/UserEdit';
import Api from '@/providers/Api.js';
import {HalfCircleSpinner} from 'epic-spinners';
import Help from '@/providers/Help';
import GetMembershipTypeOptionsMixin from '@/mixins/GetMembershipTypeOptionsMixin';
import GetMemberTypeMappingOptionsMixin from '@/mixins/GetMemberTypeMappingOptionsMixin';
import {handleServerErrorWithValidation} from '@/helpers/ToastedHelper';
import {ROLE_MSS_RECRUITER} from "@/helpers/rbacHelper";

export default Vue.extend({
  name: 'userList',
  mixins: [GetMembershipTypeOptionsMixin, GetMemberTypeMappingOptionsMixin],
  components: {
    DataTable,
    HalfCircleSpinner,
    UserEdit,
  },
  data: function () {
    return {
      users: [],
      filters: {
        email: {field: 'email', type: 'string', value: null},
        username: {field: 'username', type: 'string', value: null},
        society_id: {field: 'society_id', type: 'string', value: null},
        role: {field: 'role', type: 'string', value: null},
      },
      fields: [
        {key: 'id', sortable: true},
        {key: 'email', sortable: true, custom: true},
        {key: 'username', sortable: true, custom: true},
        {key: 'society_id', sortable: false, custom: true, label: 'Society'},
        {
          key: 'specialty_id',
          sortable: false,
          custom: true,
          label: 'Specialty',
        },
        {
          key: 'subspecialty_id',
          sortable: false,
          custom: true,
          label: 'Subspecialty',
        },
        {key: 'actions', sortable: false, custom: true},
      ],
      editedUserId: null,
      saveLoading: false,
    };
  },
  computed: {
    societyOptions: function () {
      return this.$store.state.societyOptions;
    },
    roleOptions: function () {
      return this.$store.state.roleOptions;
    },
    filteredUsers: function () {
      const filterByEmail = this.filters.email && !!this.filters.email.value;
      const filterByUsername = this.filters.username && !!this.filters.username.value;
      const filterBySocietyId =
          this.filters.society_id && !!this.filters.society_id.value;
      const filterByRole = this.filters.role && !!this.filters.role.value;
      if (filterByEmail || filterBySocietyId || filterByRole || filterByUsername) {
        return this.users.filter((u) => {
          let isMatch = true;
          if (filterByEmail) {
            isMatch &= u.email.includes(this.filters.email.value);
          }
          if (filterByUsername) {
            isMatch &= u.username ? u.username.includes(this.filters.username.value) : false;
          }
          if (filterBySocietyId) {
            isMatch &= parseInt(u.society_id) === this.filters.society_id.value;
          }
          if (filterByRole) {
            isMatch &= u.roles.includes(this.filters.role.value);
          }
          return isMatch;
        });
      } else return this.users;
    },
    editedUser: function () {
      return this.users.find(({id}) => id === this.editedUserId);
    },
  },
  created: function () {
    this.getUsers();
  },
  methods: {
    getUsers: async function () {
      this.$wait.start('loading');

      let users = await Api.getAllUsers();
      this.users = users;

      this.$wait.end('loading');

      return users;
    },
    onDeleteUser: function (user) {
      Help.confirm.show('Permanently delete this user?', 'Delete', () => {
        this.deleteUser(user);
      });
    },
    deleteUser: function (user) {
      Api.deleteUser(user.id).then(() => {
        this.removeUserFromList(user);
        const index = this.users.findIndex((u) => u.id === user.id);
        this.users.splice(index, 1);
        this.$toasted.success('User deleted', {duration: 3000});
        this.$refs.dataTable.refresh();
      });
    },
    onUpdateUser: function (updatedUser) {
      const index = this.users.findIndex(
          (u) => parseInt(u.id) === parseInt(updatedUser.id)
      );
      this.$set(this.users, index, {...this.users[index], ...updatedUser});
    },
    removeUserFromList: function (user) {
      const index = this.users.findIndex((u) => u.id === user.id);
      this.users.splice(index, 1);
    },
    saveUser: async function (user) {
      this.saveLoading = true;
      try {
        const updatedUser = await Api.updateUserByAdmin(user);
        this.$toasted.success('User saved', {duration: 3000});
        this.onUpdateUser(updatedUser);
        this.editedUserId = null;
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.saveLoading = false;
      }
    },
    saveRecruiter: async function (recruiterProfile) {
      this.saveLoading = true;
      try {
        const {user} = await Api.updateRecruiterByAdmin(recruiterProfile);
        this.$toasted.success('User saved', {duration: 3000});
        this.onUpdateUser({id: recruiterProfile.id, ...user});
        this.editedUserId = null;
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.saveLoading = false;
      }
    },
    onSaveHandler: function () {
      const userData = this.$refs.editUserForm.getFormData();
      if (!userData) return;
      this.$nextTick(() => {
        if (userData.roles.includes(ROLE_MSS_RECRUITER)) this.saveRecruiter(userData);
        else this.saveUser(userData);
      });
    },
    onResetUser: async function () {
      if (!this.editedUser) return;
      let regLink = 'no registration link';
      if (this.editedUser.member && this.editedUser.member.registration_token) {
        regLink = `${window.location.origin}/#/sign-up/${this.editedUser.member.registration_token}`;
        regLink = `<a href="${regLink}">${regLink}</a>`;
      }
      Help.confirm.show(
          `Are you sure you want to reset this user?<br>Registration Link: ${regLink}`,
          'Ok',
          () => {
            this.resetUser();
          },
          null,
          true
      );
    },
    resetUser: async function () {
      this.resetLoading = true;

      try {
        await Api.resetUser(this.editedUser.id);
        this.$toasted.success('User restored', {duration: 3000});
        this.removeUserFromList(this.editedUser);
        this.$refs.dataTable.refresh();
        this.editedUserId = null;
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.resetLoading = false;
      }
    },
  },
});
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss"></style>
